/**
 * Hook refatorado seguindo SLAP (Single Level of Abstraction)
 * Cada função tem apenas um nível de abstração
 *
 * ✅ FUNCIONALIDADE IMPLEMENTADA:
 * - Seleção automática de filial baseada no CNPJ do destinatário do XML
 * - Quando um XML é processado, o sistema verifica se o CNPJ do destinatário
 *   corresponde a alguma filial do usuário e seleciona automaticamente
 * - Notificação ao usuário quando a filial é selecionada automaticamente
 */

"use client";

import type {} from "@PN/inclusao";
import { useState, useRef, useCallback } from "react";
import { useXmlHistory, useCatalogs } from "store";
import { useSearchFornecedor } from "@PN/_nucleo";
import { toast } from "react-hot-toast";
import {
  converterXmlItemParaProdutoInclusao,
  useFetchXmlDetailsTanstack,
  identificacaoUtils,
  usePrenotaStore,
  OrigemProduto,
  type XmlDetailsResponse,
  type TotalNotaItem,
  type Produto,
  type DateISO,
  type Mode,
} from "@PN/inclusao";

// =================================================================
// TIPOS
// =================================================================

interface CurrentFornecedor {
  cod?: string;
  loja?: string;
  nome?: string;
  cnpj?: string;
}

interface XmlSearchState {
  isLoading: boolean;
  error: string | null;
  xmlData: any;
  hasFiredSearch: boolean;
  lastSearchedChave: string;
  fetchXmlAsync: (chave: string, validarInclusao?: boolean) => Promise<any>;
  resetError: () => void; // ✅ Adiciona função para resetar erro
}

interface FornecedorSearchState {
  isLoading: boolean;
  error: Error | null;
  data: any;
  hasSearched: boolean;
  searchFornecedor: (cnpj: string) => void;
}

// =================================================================
// HOOKS ESPECIALIZADOS (SLAP)
// =================================================================

/**
 * Hook para gerenciar estado de busca XML
 * Nível de abstração: Estado de busca
 */
function useXmlSearchState(): XmlSearchState {
  const [xmlData, setXmlData] = useState<any>(null);
  const hasFiredSearch = useRef(false);
  const lastSearchedChave = useRef<string>("");

  const {
    mutateAsync: fetchXmlAsync,
    isPending: isLoading,
    error,
    reset, // ✅ Adiciona função reset do TanStack Query
  } = useFetchXmlDetailsTanstack();

  return {
    isLoading,
    error: error?.message || null,
    xmlData,
    hasFiredSearch: hasFiredSearch.current,
    lastSearchedChave: lastSearchedChave.current,
    fetchXmlAsync: (chave: string, validarInclusao: boolean = true) =>
      fetchXmlAsync({ chave, validarInclusao }),
    resetError: reset, // ✅ Expõe função para resetar erro
  };
}

/**
 * Hook para gerenciar busca de fornecedor
 * Nível de abstração: Busca de fornecedor
 */
function useFornecedorSearchState(): FornecedorSearchState {
  const { searchFornecedor, isLoading, error, data } = useSearchFornecedor();

  const hasSearched = useRef(false);

  return {
    isLoading,
    error,
    data,
    hasSearched: hasSearched.current,
    searchFornecedor, // ✅ Adicionar searchFornecedor ao retorno
  };
}

/**
 * Hook para gerenciar estado do popover
 * Nível de abstração: UI do popover
 */
function usePopoverState() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [chaveParaRestaurar, setChaveParaRestaurar] = useState<string | null>(
    null
  );

  return {
    isPopoverOpen,
    setIsPopoverOpen,
    chaveParaRestaurar,
    setChaveParaRestaurar,
  };
}

// =================================================================
// SERVIÇOS ESPECIALIZADOS (TDA)
// =================================================================

/**
 * Serviço para processar dados XML
 * Nível de abstração: Processamento de dados
 */
class XmlDataProcessor {
  static processXmlData(xml: XmlDetailsResponse, chaveNFe: string) {
    const especie = this.detectEspecie(chaveNFe);
    const modo = this.detectMode(chaveNFe);
    const dataEmissao = this.formatDataEmissao(xml.dataEmissao);

    return {
      especie,
      modo,
      dataEmissao,
      xml,
    };
  }

  private static detectEspecie(chaveNFe: string): string {
    if (chaveNFe.length >= 22) {
      const modelo = chaveNFe.substring(20, 22);
      if (modelo === "57") return "CTE";
      if (modelo === "66") return "NF3E";
      if (modelo === "62") return "NFCOM";
      return "SPED";
    }
    return "SPED";
  }

  private static detectMode(chaveNFe: string): Mode {
    if (chaveNFe.length >= 22) {
      const modelo = chaveNFe.substring(20, 22);
      if (modelo === "57") return "cte";
      if (modelo === "66" || modelo === "62") return "manual"; // NF3E e NFCOM não têm XML
      return "xml";
    }
    return "xml";
  }

  private static formatDataEmissao(dataEmissao: string): DateISO {
    return identificacaoUtils.date.formatToISO(dataEmissao);
  }
}

/**
 * Serviço para seleção automática de filial
 * Nível de abstração: Seleção de filial
 */
class FilialAutoSelector {
  /**
   * Tenta selecionar automaticamente a filial baseada no CNPJ do destinatário
   */
  static selectFilialByDestinatario(
    cnpjDestinatario: string,
    filiaisUsuario: any[]
  ): { codigo: string; nome: string } | null {
    if (!cnpjDestinatario || !filiaisUsuario?.length) {
      return null;
    }

    // Normaliza o CNPJ (remove caracteres especiais)
    const cnpjLimpo = cnpjDestinatario.replace(/\D/g, "");

    console.log(
      "🔍 [FilialAutoSelector] Tentando seleção automática de filial:",
      {
        cnpjDestinatario,
        cnpjLimpo,
        filiaisDisponiveis: filiaisUsuario.length,
      }
    );

    // Busca por match exato de CNPJ
    const filialEncontrada = filiaisUsuario.find((filial) => {
      if (!filial.cgc) return false;

      const filialCnpjLimpo = filial.cgc.replace(/\D/g, "");
      const match =
        filialCnpjLimpo === cnpjLimpo || filial.cgc === cnpjDestinatario;

      if (match) {
        console.log("✅ [FilialAutoSelector] Filial encontrada por CNPJ:", {
          filialCodigo: filial.codigo,
          filialNome: filial.nome,
          filialCnpj: filial.cgc,
          cnpjDestinatario,
        });
      }

      return match;
    });

    if (filialEncontrada) {
      return {
        codigo: filialEncontrada.codigo,
        nome: filialEncontrada.nome,
      };
    }

    console.log(
      "⚠️ [FilialAutoSelector] Nenhuma filial encontrada para CNPJ:",
      cnpjDestinatario
    );
    return null;
  }
}

/**
 * Serviço para processar produtos XML
 * Nível de abstração: Processamento de produtos
 */
class XmlProductProcessor {
  static processProducts(xml: XmlDetailsResponse, chaveNFe: string): Produto[] {
    // Verifica se é NF3E ou NFCOM - esses modelos não devem ter produtos criados automaticamente
    if (chaveNFe.length >= 22) {
      const modelo = chaveNFe.substring(20, 22);
      if (modelo === "66" || modelo === "62") {
        // NF3E e NFCOM: não cria produtos automaticamente, retorna array vazio
        return [];
      }
    }

    if (Array.isArray(xml.itens) && xml.itens.length > 0) {
      return xml.itens.map((item, index) =>
        converterXmlItemParaProdutoInclusao(item, index)
      );
    } else {
      return this.createCTEProduct(xml, chaveNFe);
    }
  }

  private static createCTEProduct(
    xml: XmlDetailsResponse,
    chaveNFe: string
  ): Produto[] {
    const valorTotalNota = xml.valorTotalDaNota
      ? Number(xml.valorTotalDaNota.replace(",", "."))
      : 0;

    return [
      {
        idUnico: `xml_${chaveNFe}_001`,
        quantidade: 1,
        valorTotal: valorTotalNota,
        origem: OrigemProduto.XML,
        codProdForn: "FRETE_CTE",
        descProdForn: "Frete - Conhecimento de Transporte",
        origemProdForn: "0",
        ncmProdForn: "00000000",
        umFornecedor: "UN",
        itemNF: "001",
        qtdeFornecedor: 1,
        codProduto: "",
        descricao: "",
        origemProduto: "",
        ncmProduto: "",
        unidadeMedida: "",
        grupoTributario: "",
        tipoProduto: "",
        pedidoCompra: "",
        itemPedido: "",
        fatorConversao: undefined,
        tpFato: undefined,
        isConnected: false,
        isSelected: false,
        relevanceScore: undefined,
      },
    ];
  }
}

// =================================================================
// TRATAMENTO DE ERROS E NOTIFICAÇÕES
// =================================================================

/**
 * Gerenciador simplificado de notificações
 */
class NotificationManager {
  static success(message: string) {
    toast.success(`✅ ${message}`);
  }

  static error(message: string) {
    toast.error(`❌ ${message}`);
  }

  static info(message: string) {
    toast(`ℹ️ ${message}`, {
      icon: "ℹ️",
      style: { background: "#3b82f6", color: "white" },
    });
  }

  static warning(message: string) {
    toast(`⚠️ ${message}`, {
      icon: "⚠️",
      style: { background: "#f59e0b", color: "white" },
    });
  }

  /**
   * Exibe erro baseado no tipo
   */
  static showError(error: any) {
    const message = error?.message || "Erro desconhecido";

    if (
      message.includes("já foi processada") ||
      message.includes("já existe")
    ) {
      this.warning("Esta chave já foi processada anteriormente no sistema");
    } else if (
      message.includes("não foi liberado") ||
      message.includes("não foi possível carregar")
    ) {
      this.warning(
        "Documento ainda não foi liberado no SEFAZ. Tente novamente em alguns minutos."
      );
    } else if (message.includes("Chave da NFe é obrigatória")) {
      this.error("Por favor, informe a chave da NFe");
    } else if (message.includes("formato inválido")) {
      this.error("Formato da chave inválido. Verifique se está correto");
    } else if (message.includes("fetch") || message.includes("network")) {
      this.error("Erro de conexão. Verifique sua internet e tente novamente");
    } else {
      this.error(message);
    }
  }
}

// =================================================================
// HOOK PRINCIPAL REFATORADO
// =================================================================

export function useChaveNfeCard() {
  // Estados especializados
  const xmlSearch = useXmlSearchState();
  const fornecedorSearch = useFornecedorSearchState();
  const popover = usePopoverState();

  const { draft, actions, hasHydrated } = usePrenotaStore();
  const { xmlHistory, addToHistory } = useXmlHistory();
  const { filiais } = useCatalogs();

  // =================================================================
  // FUNÇÃO PARA LIMPAR CHAVE APÓS PROCESSAMENTO
  // =================================================================

  /**
   * Reseta apenas o erro após processamento bem-sucedido (mantém a chave)
   */
  const clearErrorAfterSuccess = useCallback(() => {
    // ✅ RESET DO ERRO: Limpa apenas estado de erro após sucesso
    xmlSearch.resetError();
  }, [xmlSearch]);

  // =================================================================
  // FUNÇÕES DE ALTO NÍVEL (SLAP)
  // =================================================================

  /**
   * Função de alto nível: Atualizar store
   */
  const updateStore = useCallback(
    (
      processedData: any,
      products: Produto[],
      xml: XmlDetailsResponse,
      chaveNFe: string
    ) => {
      console.log("🔍 [updateStore] Iniciando atualização da store", {
        hasHydrated,
        hasActions: !!actions?.draft?.documento?.set,
        xml: xml.numero,
        chaveNFe,
        productsCount: products.length,
      });

      if (!hasHydrated || !actions?.draft?.documento?.set) {
        console.warn("❌ [updateStore] Store não está pronta", {
          hasHydrated,
          hasActions: !!actions?.draft?.documento?.set,
        });
        return;
      }

      // ✅ SELEÇÃO AUTOMÁTICA DE FILIAL: Baseada no CNPJ do destinatário
      let filialSelecionada = null;
      const filiaisDisponiveis = filiais?.all || [];
      if (xml.cnpjDestinatario && filiaisDisponiveis.length > 0) {
        console.log("🔍 [updateStore] Tentando seleção automática de filial:", {
          cnpjDestinatario: xml.cnpjDestinatario,
          filiaisDisponiveis: filiaisDisponiveis.length,
          filiais: filiaisDisponiveis.map((f) => ({
            codigo: f.codigo,
            nome: f.nome,
            cnpj: f.cgc,
          })),
        });

        filialSelecionada = FilialAutoSelector.selectFilialByDestinatario(
          xml.cnpjDestinatario,
          filiaisDisponiveis
        );

        if (filialSelecionada) {
          console.log(
            "✅ [updateStore] Filial selecionada automaticamente:",
            filialSelecionada
          );
        } else {
          console.log(
            "⚠️ [updateStore] Nenhuma filial encontrada para CNPJ:",
            xml.cnpjDestinatario
          );
        }
      } else {
        console.log(
          "🔍 [updateStore] Condições não atendidas para seleção automática:",
          {
            temCnpjDestinatario: !!xml.cnpjDestinatario,
            cnpjDestinatario: xml.cnpjDestinatario,
            temFiliais: !!filiais?.all,
            filiaisCount: filiais?.all?.length || 0,
          }
        );
      }

      // ✅ USAR TOTAIS DIRETOS DA API (mais preciso que calcular dos produtos)
      const xmlData = xml as any; // Type assertion temporária
      const totaisDocumento = {
        despesa: Number(xmlData.valorOutros?.replace(",", ".")) || 0,
        frete: Number(xmlData.valorFrete?.replace(",", ".")) || 0,
        desconto: Number(xmlData.valorDesconto?.replace(",", ".")) || 0,
      };

      // ✅ CRIAR OBJETO COM TODOS OS CUSTOS DA NOTA FISCAL (TOTAL = SUBTOTAL/BRUTO)
      const totalNota: TotalNotaItem = {
        // Salva o TOTAL BRUTO (subtotal)
        total: xmlData.valorTotalDaNota || "0",
        // Mantém o valor do desconto para exibição/uso em UI
        desconto: xmlData.valorDesconto || "0",
        frete: xmlData.valorFrete || "0",
        despesa: xmlData.valorOutros || "0",
        icms: xmlData.valorIcms || "0",
        ipi: xmlData.valorIpi || "0",
        pis: xmlData.valorPis || "0",
        cofins: xmlData.valorCofins || "0",
      };

      console.log("✅ [updateStore] Totais da API para o documento:", {
        totaisDocumento,
        totalNota,
        xml: {
          valorOutros: xmlData.valorOutros,
          valorFrete: xmlData.valorFrete,
          valorDesconto: xmlData.valorDesconto,
          valorIcms: xmlData.valorIcms,
          valorIpi: xmlData.valorIpi,
          valorPis: xmlData.valorPis,
          valorCofins: xmlData.valorCofins,
        },
      });

      // Atualizar documento
      const documentoData = {
        numero: xml.numero || "",
        serie: xml.serie || "",
        especie: processedData.especie,
        chaveNFe: chaveNFe, // ✅ Usar o parâmetro chaveNFe em vez de xml.chaveNFe
        dataEmissao: processedData.dataEmissao,
        fornecedor: draft.documento.fornecedor ?? "",
        loja: draft.documento.loja ?? "",
        despesa: totaisDocumento.despesa,
        frete: totaisDocumento.frete,
        desconto: totaisDocumento.desconto,
      };

      console.log("✅ [updateStore] Atualizando documento:", documentoData);
      actions.draft.documento.set(documentoData);

      // ✅ ATUALIZAR AUX COM TOTAIS DA NOTA FISCAL
      actions.aux.setTotalNota(totalNota);

      // Atualizar produtos
      if (
        actions.aux?.produtoTabela?.clear &&
        actions.aux?.produtoTabela?.add
      ) {
        actions.aux.produtoTabela.clear();
        products.forEach((produto) => actions.aux.produtoTabela.add(produto));
      }

      // Atualizar fornecedor
      if (actions.aux?.fornecedor?.set) {
        const fornecedorData = {
          cnpj: xml.cnpjEmitente || "",
          nome: xml.nomeEmitente || "",
        };
        console.log(
          "✅ [updateStore] Atualizando fornecedor aux:",
          fornecedorData
        );
        actions.aux.fornecedor.set(fornecedorData);

        // Verificar se foi salvo corretamente
        setTimeout(() => {
          const state = usePrenotaStore.getState();
          console.log("🔍 [updateStore] Estado do fornecedor após salvar:", {
            cnpj: state.aux?.fornecedor?.cnpj,
            nome: state.aux?.fornecedor?.nome,
          });
        }, 100);
      } else {
        console.warn(
          "❌ [updateStore] actions.aux?.fornecedor?.set não disponível"
        );
      }

      // ✅ ATUALIZAR FILIAL: Se encontrada automaticamente, usa setFilial
      if (filialSelecionada && actions.draft.setFilial) {
        console.log(
          "✅ [updateStore] Definindo filial automaticamente:",
          filialSelecionada.codigo
        );
        actions.draft.setFilial(filialSelecionada.codigo);

        // ✅ NOTIFICAÇÃO SILENCIOSA: Filial selecionada automaticamente (sem toast)
        console.log(
          `✅ [updateStore] Filial "${filialSelecionada.nome}" selecionada automaticamente baseada no CNPJ do destinatário`
        );
      }

      // Atualizar modo
      actions.aux.setMode(processedData.modo);

      // ✅ SALVAR DADOS COMPLETOS DO ARQUIVEI NA STORE
      if (actions.aux?.setDadosArquivei && processedData.dadosArquivei) {
        actions.aux.setDadosArquivei(processedData.dadosArquivei);
        console.log(
          "✅ [updateStore] Dados do Arquivei salvos na store:",
          processedData.dadosArquivei
        );
      }

      // ✅ CALCULAR E SALVAR VALOR TOTAL NA STORE AUX
      if (typeof actions.aux?.setTotalNota === "function") {
        // ✅ USAR VALOR TOTAL DA NOTA DO XML EM VEZ DE CALCULAR
        let valorTotalCalculado = 0;
        let valorTotalItens = 0;

        // Calcula o valor total a partir dos itens para validação
        if (products.length > 0) {
          valorTotalItens = products.reduce((total, produto) => {
            return total + (produto.valorTotal || 0);
          }, 0);
        }

        if (xml.valorTotalDaNota) {
          // Converte o valor total da nota do XML (formato brasileiro "2063.70")
          valorTotalCalculado =
            Number(xml.valorTotalDaNota.replace(",", ".")) || 0;

          console.log("✅ [updateStore] Usando valor total da nota do XML:", {
            valorTotalDaNota: xml.valorTotalDaNota,
            valorTotalCalculado,
            valorTotalItens,
            produtosCount: products.length,
          });

          // ✅ VALIDAÇÃO: Verifica se o valor total dos itens bate com o valor total da nota
          const diferenca = Math.abs(valorTotalCalculado - valorTotalItens);
          const tolerancia = 0.01; // Tolerância de 1 centavo para diferenças de arredondamento

          if (diferenca > tolerancia) {
            console.warn(
              "⚠️ [updateStore] Diferença entre valor total da nota e soma dos itens:",
              {
                valorTotalDaNota: valorTotalCalculado,
                valorTotalItens,
                diferenca,
                tolerancia,
              }
            );
          } else {
            console.log(
              "✅ [updateStore] Validação OK: valor total da nota confere com soma dos itens"
            );
          }
        } else if (products.length > 0) {
          // Fallback: calcula a partir dos itens se não houver valor total da nota
          valorTotalCalculado = valorTotalItens;

          console.log(
            "⚠️ [updateStore] Fallback: calculando valor total a partir dos itens:",
            {
              valorTotalCalculado,
              produtosCount: products.length,
            }
          );
        }

        if (valorTotalCalculado > 0) {
          console.log("✅ [updateStore] Salvando valor total na store aux:", {
            valorTotalCalculado,
            produtos: products.map((p) => ({
              id: p.idUnico,
              valor: p.valorTotal,
              descricao: p.descProdForn,
            })),
          });

          // actions.aux.setTotalNota(valorTotalCalculado); // ✅ Removido - já salvo acima

          // Verificar se foi salvo corretamente
          setTimeout(() => {
            const state = usePrenotaStore.getState();
            console.log("🔍 [updateStore] Estado do valor total após salvar:", {
              totalNota: state.aux?.totalNota,
              valorTotalCalculado,
            });
          }, 100);
        } else {
          console.warn(
            "⚠️ [updateStore] Valor total calculado é zero ou inválido"
          );
        }
      } else {
        console.warn(
          "❌ [updateStore] actions.aux?.setTotalNota não disponível"
        );
      }
    },
    [
      hasHydrated,
      actions,
      draft.documento.fornecedor,
      draft.documento.loja,
      filiais?.all,
    ]
  );

  /**
   * Função de alto nível: Processar dados XML
   */
  const processXmlData = useCallback(
    async (xml: XmlDetailsResponse, chaveNFe: string) => {
      try {
        console.log("🔍 [processXmlData] Processando XML:", {
          chaveNFe,
          xmlNumero: xml.numero,
        });

        const processedData = XmlDataProcessor.processXmlData(xml, chaveNFe);
        const products = XmlProductProcessor.processProducts(xml, chaveNFe);

        console.log("✅ [processXmlData] Dados processados:", {
          processedData,
          productsCount: products.length,
        });

        updateStore(processedData, products, xml, chaveNFe);

        // ✅ BUSCA AUTOMÁTICA DE FORNECEDOR: Chama diretamente quando CNPJ é preenchido
        if (xml.cnpjEmitente && xml.cnpjEmitente.trim().length > 0) {
          console.log(
            "🚀 [processXmlData] Iniciando busca automática de fornecedor por CNPJ:",
            xml.cnpjEmitente
          );
          fornecedorSearch.searchFornecedor(xml.cnpjEmitente.trim());
        } else {
          console.log(
            "🔍 [processXmlData] CNPJ não disponível para busca automática:",
            {
              cnpjEmitente: xml.cnpjEmitente,
              cnpjLength: xml.cnpjEmitente?.length,
            }
          );
        }

        // ✅ NOTIFICAÇÃO ÚNICA: Sucesso é mostrado apenas no final do processo
        // NotificationManager.success("XML processado com sucesso!");
      } catch (error) {
        console.error("❌ [processXmlData] Erro ao processar XML:", error);
        NotificationManager.showError(error);
      }
    },
    [updateStore, fornecedorSearch]
  );

  /**
   * Função de alto nível: Buscar e processar XML
   */
  const handleSearchAndPopulate = useCallback(
    async (chave: string, validarInclusao: boolean = true) => {
      try {
        const validation = identificacaoUtils.xml.validateKey(chave);
        if (!validation.isValid) {
          NotificationManager.warning(`Chave inválida: ${validation.error}`);
          return null;
        }

        const chaveLimpa = identificacaoUtils.xml.cleanKey(chave);

        // Mostrar toast de carregamento
        const loadingToast = toast.loading("🔍 Buscando XML...");

        try {
          const xmlData = await xmlSearch.fetchXmlAsync(
            chaveLimpa,
            validarInclusao
          );

          if (xmlData) {
            await processXmlData(xmlData, chaveLimpa);
            addToHistory(chaveLimpa);

            // ✅ RESET ERRO: Limpar apenas erro após processamento bem-sucedido (mantém chave)
            clearErrorAfterSuccess();

            // Fechar toast de carregamento e mostrar sucesso
            toast.dismiss(loadingToast);
            NotificationManager.success("XML carregado com sucesso!");

            return xmlData;
          } else {
            // Fechar toast de carregamento e mostrar aviso
            toast.dismiss(loadingToast);
            NotificationManager.warning(
              "Nenhum dado encontrado para esta chave"
            );
            return null;
          }
        } catch (error) {
          // Fechar toast de carregamento e mostrar erro
          toast.dismiss(loadingToast);
          NotificationManager.showError(error);
          return null;
        }
      } catch (error) {
        console.error("❌ [handleSearchAndPopulate] Erro geral:", error);
        NotificationManager.showError(error);
        return null;
      }
    },
    [xmlSearch, addToHistory, processXmlData, clearErrorAfterSuccess]
  );

  // =================================================================
  // RETORNO SIMPLIFICADO
  // =================================================================

  return {
    // Estados
    isLoading: xmlSearch.isLoading || fornecedorSearch.isLoading,
    error: xmlSearch.error || fornecedorSearch.error?.message || null,
    xmlData: xmlSearch.xmlData,
    xmlHistory,
    isPopoverOpen: popover.isPopoverOpen,

    // Ações
    handleManualSearch: handleSearchAndPopulate,
    setIsPopoverOpen: popover.setIsPopoverOpen,

    // Computed
    tipoChave:
      identificacaoUtils.xml.detectTypeByData(xmlSearch.xmlData)?.tipo || "",
    completion: {
      allValid: !!(
        draft.documento.numero &&
        draft.documento.serie &&
        draft.documento.chaveNFe &&
        draft.documento.fornecedor &&
        draft.documento.loja
      ),
      someValid: !!(
        draft.documento.numero ||
        draft.documento.serie ||
        draft.documento.chaveNFe ||
        draft.documento.fornecedor ||
        draft.documento.loja
      ),
    },
  };
}
